import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import useSendEmailToContacts from './sendEmailToContacts';
import ExportContactsButton from 'components/Dashboard/Influence/CrmContacts/ExportContactsButton';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import { useMemo } from 'react';
import InfluenceNotIncludedFeaturesPopup from 'components/Dashboard/Influence/CrmContacts/InfluenceNotIncludedFeaturesPopup';
import { useLocation, useNavigate, useParams } from 'react-router';
import RemoveTagsFromContacts from './RemoveTagsFromContacts';
import { AddContactsToGroupButton } from 'components/Dashboard/Influence/CrmGroups/AddContactsToGroup';
import RemoveContactsFromGroupModal from './RemoveContactsFromGroupModal';
import LogInteractionModal from 'components/Dashboard/Influence/CrmInteractions/LogInteractionModal/LogInteractionModal';
import getContactFiltersExcludingStaff from 'components/Dashboard/Influence/CrmContacts/getContactFiltersExcludingStaff';

const CrmContactsListBar = ({
  setSelectedCrmContacts,
  totalHits,
  setShowAddTagsModal,
  setShowRemoveContactsModal,
  setShowRemoveContactTagsModal,
  isLoading,
  setIsLoading,
}) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { listDomainsVerified, selectedCrmContacts, previousStoredSelectedCrmContacts, activePlan, group } = state;
  const { contentResults } = useContentResults();
  const { CrmContacts } = contentResults ?? {};
  const { hits } = CrmContacts ?? {};

  const [isLoadingSendEmail, setIsLoadingSendEmail] = useState(false);
  const [expandedButton, setExpandedButton] = useState('');
  const [showLogInteractionModal, setShowLogInteractionModal] = useState(false);
  const [isShowRemoveContactsFromGroupModal, setIsShowRemoveContactsFromGroupModal] = useState();

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { groupId } = params;

  const { enableCrm, neverShowTrialBanner } = activePlan;
  const [showCrmPopup, setShowCrmPopup] = useState(false);

  const query = new URLSearchParams(location.search);
  const includeStaff = query.get('includeStaff') === 'true';
  const { getSectionFilters } = useGetSectionFilters('InfluenceContacts');
  const sectionFilters = getSectionFilters();
  const { filters } = sectionFilters;

  const source = new AbortController();

  const isSmallMobile = useIsMobile(1060);
  const onlyIconOnMobile = useIsMobile(677);

  const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;

  const isSelected = selectedIds.length > 0 || selectedAll || selectedAllVisible.length > 0;

  const selectAllContacts = () => {
    setSelectedCrmContacts({
      selectedAll: true,
      selectedIds: [],
      selectedAllVisible: [],
    });
  };

  useEffect(() => {
    if (!isSelected) setExpandedButton('');
  }, [isSelected]);

  const selectedQnttyDecreased = useMemo(
    () => previousStoredSelectedCrmContacts.selectedIds.length > selectedIds.length,
    [previousStoredSelectedCrmContacts, selectedIds]
  );

  const generateHelperText = () => {
    if (selectedIds.length > 0) {
      return (
        <p className='contacts-list-bar-helper-text'>
          <span
            key={selectedIds.length}
            className={`${selectedQnttyDecreased ? 'selected-qntty-decreased' : 'selected-qntty'} mr-2`}
          >
            {thousandCommas(selectedIds.length)}
          </span>
          <span className='selected-qntty'>selected</span>
        </p>
      );
    } else if (selectedAll) {
      return (
        <p className='contacts-list-bar-helper-text'>
          <span className='selected-qntty'>
            {thousandCommas(totalHits)}
            {totalHits >= 10000 ? '+' : ''} selected
          </span>
        </p>
      );
    } else if (selectedAllVisible.length > 0) {
      return (
        <p className='contacts-list-bar-helper-text'>
          <span className='selected-qntty'>{thousandCommas(selectedAllVisible.length)} selected.</span>
          {selectedAllVisible.length < totalHits && (
            <span className='contacts-list-bar-helper-text-link' onClick={selectAllContacts}>
              Select all contacts ({totalHits}).
            </span>
          )}
        </p>
      );
    } else {
      return (
        <p className='contacts-list-bar-helper-text'>
          <span>Select contacts to send emails, add tags and take other actions</span>
        </p>
      );
    }
  };

  const handleOnClick = (button, action) => {
    if (onlyIconOnMobile && expandedButton !== button) {
      setExpandedButton(button);
    } else {
      setExpandedButton('');
      return action();
    }
  };

  const { sendEmailToContacts } = useSendEmailToContacts();
  const selectedIdsWithEmail = (selectedAllVisible.length > 0 ? selectedAllVisible : selectedIds).filter(
    (id) => !!hits.find((hit) => hit.id === id)?.email
  );
  // ponytail: selectedAll (query-based, unbounded) can't be filtered by email client-side, backend adds all matches
  const noneSelectedHaveEmail = !selectedAll && isSelected && selectedIdsWithEmail.length === 0;
  const handleSendEmail = () => {
    let contactsToSendIds = selectedAllVisible.length > 0 || selectedIds.length > 0 ? selectedIdsWithEmail : 'all';
    sendEmailToContacts({
      contactsToSendIds,
      query,
      requestSource: source,
      filters,
      setIsLoading: (val) => {
        setIsLoading(val);
        setIsLoadingSendEmail(val);
      },
    });
  };

  const unverifiedEmail =
    !listDomainsVerified.verifiedDomains.length &&
    !listDomainsVerified.verifiedEmailAddresses.length &&
    !neverShowTrialBanner;

  // selectedAll spans the whole filtered result set (potentially way beyond what's loaded here),
  // so there's nothing to prefill for that case - only an explicit id-by-id selection can be mapped
  // to the full contact records needed by the interaction form's Stakeholders field. Only worth
  // building once the modal is actually about to render - selectedCrmContacts changes on every
  // checkbox click, and CrmContacts.hits can run into the thousands, so doing this filter/map on
  // every click regardless of whether the modal is even open was needless work on every selection.
  const selectedContactIds = selectedAllVisible.length > 0 ? selectedAllVisible : selectedIds;
  const preselectedStakeholders = showLogInteractionModal
    ? (CrmContacts?.hits ?? [])
        .filter((hit) => selectedContactIds.includes(hit.id))
        .map((hit) => ({
          value: hit.id,
          id: hit.id,
          label: hit.title,
          imageId: hit.imageId,
          imageUrl: hit.imageUrl,
          email: hit.email,
        }))
    : [];

  // selectedAll's contacts aren't loaded client-side (see above) - hand the modal the same
  // filters/query the Directory list itself is using so it can resolve the matching contacts
  // server-side, the same way the "select all" bulk email flow does via add-contacts-by-query.
  // Staffers are hidden from the Directory by default, so exclude them here too or "select all"
  // resolves to more contacts than the Directory ever showed.
  const filtersForLogInteractionQuery = groupId ? filters : getContactFiltersExcludingStaff(filters, includeStaff);
  const stakeholdersFilterQuery =
    showLogInteractionModal && selectedAll
      ? { filters: filtersForLogInteractionQuery, query: query.get('search') || '' }
      : null;

  return (
    <>
      <div className='crm-contacts-list-bar'>
        <div className='contacts-list-bar-helper-text-wrapper'>{generateHelperText()}</div>
        {!!isSelected && (
          <>
            <div
              className={`contacts-list-bar-action-container ${
                expandedButton !== '' ? 'action-container-expanded' : ''
              }`}
            >
              {!groupId && <AddContactsToGroupButton />}
              <button
                className={`contacts-list-bar-action-container-btn ${
                  expandedButton === 'log' ? 'action-container-btn-expanded' : ''
                }`}
                disabled={isLoading}
                onClick={() => handleOnClick('log', () => setShowLogInteractionModal(true))}
              >
                <span className={`icon-chat ${isLoading ? 'icon-chat-disabled' : ''} title-h4`} />
                {onlyIconOnMobile && expandedButton !== 'log' ? '' : 'Log interaction'}
              </button>
              <button
                className={`contacts-list-bar-action-container-btn  ${
                  expandedButton === 'send' ? 'action-container-btn-expanded' : ''
                }`}
                disabled={isLoading || noneSelectedHaveEmail}
                onClick={() => {
                  if (enableCrm) {
                    handleOnClick('send', () =>
                      unverifiedEmail
                        ? navigate(`/people/emails`, {
                            state: { setUpDomain: true },
                            replace: true,
                          })
                        : handleSendEmail()
                    );
                  } else {
                    setShowCrmPopup(true);
                  }
                }}
                data-tooltip-content={
                  noneSelectedHaveEmail
                    ? 'None of the selected contacts have an email address'
                    : unverifiedEmail
                      ? 'Configure an email domain in order to send emails'
                      : undefined
                }
                data-tooltip-id={noneSelectedHaveEmail || unverifiedEmail ? 'general-tooltip' : undefined}
                data-tooltip-class-name={
                  noneSelectedHaveEmail || unverifiedEmail ? 'header-tooltip pricing-tooltip-description' : undefined
                }
                data-tooltip-place='bottom'
              >
                {isLoadingSendEmail ? (
                  <i className='fas fa-spinner fa-spin' style={{ marginRight: '2px' }}></i>
                ) : (
                  <span className={`icon-email ${isLoading ? 'icon-email-disabled' : ''}`} />
                )}
                {onlyIconOnMobile && expandedButton !== 'send' ? '' : 'Create email'}
                {unverifiedEmail && !onlyIconOnMobile && <span className='icon-info text-red'></span>}
              </button>
              <div onClick={() => !isLoading && handleOnClick('export', () => {})}>
                <ExportContactsButton
                  onlyIcon={onlyIconOnMobile && expandedButton !== 'export'}
                  contactListBar
                  isLoading={isLoading}
                />
              </div>
              {groupId && group?.type === 'Static' && (
                <button
                  className='contacts-list-bar-action-container-btn remove-contact-from-group-button'
                  onClick={() => {
                    setIsShowRemoveContactsFromGroupModal(true);
                  }}
                >
                  <span className={`icon-remove-stakeholder`} />
                  Remove from group
                </button>
              )}
              <RemoveTagsFromContacts
                expandedButton={expandedButton}
                isLoading={isLoading}
                onlyIconOnMobile={onlyIconOnMobile}
                isSmallMobile={isSmallMobile}
                handleOnClick={handleOnClick}
                setShowRemoveContactsModal={setShowRemoveContactsModal}
                setShowRemoveContactTagsModal={setShowRemoveContactTagsModal}
                setShowAddTagsModal={setShowAddTagsModal}
                setShowChampionsStatusModal={() => {
                  dispatch({
                    type: 'MODIFY_SECTION',
                    parameter: 'showChangeChampionStatusModal',
                    value: true,
                  });
                }}
              />
            </div>
            <div className='rounded-pill bg-main-white general-button specific-x-padding-12 specific-y-padding-3 flex-centered'>
              <span className='icon-search light-50-text mr-2' />
              <input
                className='input-empty paragraph-p1'
                placeholder='Search'
                onKeyDown={(e) => {
                  const value = e.target.value?.trim();
                  if (e.keyCode === 13) {
                    const params = new URLSearchParams(location.search);
                    if (value === '') {
                      params.delete('search');
                    } else {
                      params.set('search', encodeURIComponent(value));
                    }
                    navigate({ search: `?${params.toString()}` });
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
      <InfluenceNotIncludedFeaturesPopup type={'contacts'} setShowPopup={setShowCrmPopup} showPopup={showCrmPopup} />
      <RemoveContactsFromGroupModal
        isOpen={isShowRemoveContactsFromGroupModal}
        setIsOpen={setIsShowRemoveContactsFromGroupModal}
      />
      {showLogInteractionModal && (
        <LogInteractionModal
          onClose={() => setShowLogInteractionModal(false)}
          onSuccess={() => setShowLogInteractionModal(false)}
          preselectedStakeholders={preselectedStakeholders}
          stakeholdersFilterQuery={stakeholdersFilterQuery}
        />
      )}
    </>
  );
};

export default CrmContactsListBar;
